import { useCallback } from "react";

const COOKIE_NAME = "cookie-consent-status";
const REJECTED_KEY = "cookies-rejected";
const SESSION_KEY = "cookie-consent-session-id";
const COOKIE_DAYS = 150;

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? decodeURIComponent(match[2]) : null;
}

function setCookie(name: string, value: string, days: number) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires};path=/;SameSite=Lax`;
}

function deleteCookie(name: string) {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;SameSite=Lax`;
}

function getSessionId(): string {
  let sessionId = localStorage.getItem(SESSION_KEY);
  if (!sessionId) {
    sessionId = `session_${crypto.randomUUID()}`;
    localStorage.setItem(SESSION_KEY, sessionId);
  }
  return sessionId;
}

export type ConsentStatus = "all" | "necessary" | null;

export function useCookieConsent() {
  const getConsentStatus = useCallback((): ConsentStatus => {
    const cookieValue = getCookie(COOKIE_NAME);
    if (cookieValue === "true") return "all";
    const rejected = localStorage.getItem(REJECTED_KEY);
    if (rejected === "true") return "necessary";
    return null;
  }, []);

  const areCookiesAllowed = useCallback((): boolean => {
    return getConsentStatus() === "all";
  }, [getConsentStatus]);

  const acceptAll = useCallback(() => {
    setCookie(COOKIE_NAME, "true", COOKIE_DAYS);
    localStorage.removeItem(REJECTED_KEY);
    window.dispatchEvent(
      new CustomEvent("cookieConsentChanged", { detail: { accepted: true } })
    );
  }, []);

  const acceptNecessaryOnly = useCallback(() => {
    deleteCookie(COOKIE_NAME);
    localStorage.setItem(REJECTED_KEY, "true");
    window.dispatchEvent(
      new CustomEvent("cookieConsentChanged", { detail: { accepted: false } })
    );
  }, []);

  const acceptCustom = useCallback((analytics: boolean) => {
    if (analytics) {
      setCookie(COOKIE_NAME, "true", COOKIE_DAYS);
      localStorage.removeItem(REJECTED_KEY);
    } else {
      deleteCookie(COOKIE_NAME);
      localStorage.setItem(REJECTED_KEY, "true");
    }
    window.dispatchEvent(
      new CustomEvent("cookieConsentChanged", {
        detail: { accepted: analytics },
      })
    );
  }, []);

  const logConsentToAuditTrail = useCallback(
    async (accepted: boolean, categories?: Record<string, boolean>) => {
      try {
        const sessionId = getSessionId();
        await fetch(`${SUPABASE_URL}/functions/v1/log-cookie-consent`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            apikey: SUPABASE_KEY,
          },
          body: JSON.stringify({
            accepted,
            categories: categories || {
              necessary: true,
              analytics: accepted,
            },
            sessionId,
            userId: null,
          }),
        });
      } catch (err) {
        console.error("Failed to log consent:", err);
      }
    },
    []
  );

  const setCookieIfAllowed = useCallback(
    (
      name: string,
      value: string,
      days = 30,
      isEssential = false
    ): boolean => {
      if (isEssential || areCookiesAllowed()) {
        setCookie(name, value, days);
        return true;
      }
      return false;
    },
    [areCookiesAllowed]
  );

  return {
    getConsentStatus,
    areCookiesAllowed,
    acceptAll,
    acceptNecessaryOnly,
    acceptCustom,
    logConsentToAuditTrail,
    setCookieIfAllowed,
  };
}
